/* eslint-disable react-hooks/exhaustive-deps */
import DirectorNotesButton from "components/DirectorNotesButton";
import SuspectsChip from "components/SuspectsChip";
import TabNav from "components/TabNav";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import MainLayout from "layouts/MainLayout";
import ChangeOfVenueTab from "pages/Doubtful/ChangeOfVenueTab";
import DoesntChargeTab from "pages/Doubtful/DoesntChargeTab";
import DoubtfulTab from "pages/Doubtful/DoubtfulTab";
import FurtherInvestigationTab from "pages/FurtherInvestigationList/FurtherInvestigationTab";
import IncidentDetail from "pages/Incident/IncidentDetail/IncidentDetail";
import InstituteOfChargeTab from "pages/InstituteOfCharge/InstituteOfChargeTab";
import PrivateProsecutionTab from "pages/PrivateProsecution/PrivateProsecutionTab";
import SuspectToWitnessTab from "pages/SuspectToWitness/SuspectToWitnessTab";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchChangeOfVenueByCRNumberAndIncidentRegNo,
  fetchSuspectToWitnessByCRNumberAndIncidentRegNo,
} from "services/caseServices";
import { fetchInstructionsByCRNumberAndIncidentRegNo } from "services/doubtfulService";
import { fetchReceivingInvestigationFileById } from "services/prosecutorPoliceService";
import {
  ChangeOfVenue,
  DoubtfulCase,
  FEEDBACK_MODEL,
  InvestigationFile,
  Roles,
  SuspectTowitness,
} from "types";
import { errorHandler, hasRole } from "utils/helper";
import AssignedToTab from "./fragment/AssignedToTab";
import InvestigationInformationTab from "./fragment/InvestigationInformationTab";

const InvestigationFileDetail = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [file, setFile] = React.useState<InvestigationFile>(state);
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(false);

  const TAB_INVESTIGATION_FILE = t("investigation_file");
  const TAB_ABOUT_INVESTIGATION_FILE = t("about_investigation_file");
  const TAB_ASSIGNED_TO = t("assigned_to");
  const TAB_RE_ASSIGN_PROSECUTOR = t("re_assign_prosecutor");
  const TAB_FURTHER_INVESTIGATION = t("further_investigation");
  const TAB_CHARGE = t("charge");
  const TAB_SUSPECT_TO_WITNESS = t("suspect_to_witness_tab");
  const TAB_DOUBTFUL = t("doubtful");
  const TAB_DOESNT_INSTITUTE_CHARGE = t("doesnt_institute_charge");
  const TAB_PRIVATE_PRISECUTION = t("private_prosecution");
  const TAB_CHANGE_OF_VENUE = t("change_of_venue");

  const [activeTab, setActiveTab] = useState(TAB_INVESTIGATION_FILE);

  const [suspectToWitness, setSuspectToWitness] = useState<SuspectTowitness[]>(
    [],
  );
  const [doubtful, setDoubtful] = useState<DoubtfulCase[]>([]);
  const [venue, setVenue] = useState<ChangeOfVenue[]>([]);

  const fetchData = (id: number) => {
    fetchReceivingInvestigationFileById(id)
      .then((data) => {
        setFile(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  const fetchSuspectToWitnessData = (
    crimeReferenceNumber: string,
    incidentRegistrationNumber: string,
  ) => {
    fetchSuspectToWitnessByCRNumberAndIncidentRegNo(
      crimeReferenceNumber || "",
      incidentRegistrationNumber || "",
    )
      .then((data) => {
        setSuspectToWitness(data);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
      });
  };

  const fetchDoubtfulData = (
    crimeReferenceNumber: string,
    incidentRegistrationNumber: string,
  ) => {
    fetchInstructionsByCRNumberAndIncidentRegNo(
      crimeReferenceNumber || "",
      incidentRegistrationNumber || "",
    )
      .then((data: DoubtfulCase[]) => {
        setDoubtful(data);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
      });
  };

  const fetchVenueData = (
    crimeReferenceNumber: string,
    incidentRegistrationNumber: string,
  ) => {
    fetchChangeOfVenueByCRNumberAndIncidentRegNo(
      crimeReferenceNumber || "",
      incidentRegistrationNumber || "",
    )
      .then((data: ChangeOfVenue[]) => {
        setVenue(data);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
      });
  };

  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isProsecutorRole = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrarRole = hasRole(loginData?.roles, [Roles.registrar]);

  useEffect(() => {
    setLoading(true);
    fetchData(state?.id);
  }, [state, refetch]);

  useEffect(() => {
    if (file) {
      fetchSuspectToWitnessData(
        file.crime_reference_number,
        file.incident_registration_number,
      );
      fetchDoubtfulData(
        file.crime_reference_number,
        file.incident_registration_number,
      );
      fetchVenueData(
        file.crime_reference_number,
        file.incident_registration_number,
      );
    }
  }, [file]);

  const buildTabs = () => {
    if (!file) return [TAB_INVESTIGATION_FILE];

    const f: any = file;

    const hasNonEmptyString = (v: any) =>
      typeof v === "string" && v.trim().length > 0;

    const hasArrayWithItems = (v: any) => Array.isArray(v) && v.length > 0;

    const hasTabData = (tab: string) => {
      switch (tab) {
        case TAB_PRIVATE_PRISECUTION:
          return !!f.private_prosecutions && f.private_prosecutions.length > 0;
        case TAB_SUSPECT_TO_WITNESS:
          return suspectToWitness.length > 0;
        case TAB_DOUBTFUL:
          return doubtful.length > 0;
        case TAB_CHANGE_OF_VENUE:
          return venue.length > 0;
        case TAB_DOESNT_INSTITUTE_CHARGE: {
          const dr =
            f.decision_reason ??
            f.decision?.reason ??
            f.decision?.decision_reason ??
            f.decision_reason_text ??
            f.decisionReason ??
            f.decision?.reason_text ??
            f.decision?.text ??
            f.decision?.description ??
            f.decision_reason_list;
          if (hasNonEmptyString(dr)) return true;
          if (hasArrayWithItems(dr)) return true;
          return !!dr;
        }
        default:
          return true;
      }
    };

    const tabs: string[] = [TAB_INVESTIGATION_FILE];

    if (file?.status === "assigned_to_clerk") return tabs;

    const hasAssignedClerk = !!(
      f?.assigned_clerk ??
      f?.assign_clerk ??
      f?.clerk_assigned ??
      f?.assignedToClerk
    );
    if (!hasAssignedClerk) return tabs;

    tabs.push(TAB_ABOUT_INVESTIGATION_FILE);

    if (!file?.assign_prosecutor) {
      if (isCoordinator) tabs.push(TAB_ASSIGNED_TO);
    } else {
      if (isCoordinator) tabs.push(TAB_RE_ASSIGN_PROSECUTOR);
    }

    tabs.push(TAB_FURTHER_INVESTIGATION, TAB_CHARGE);

    if (hasTabData(TAB_PRIVATE_PRISECUTION)) tabs.push(TAB_PRIVATE_PRISECUTION);
    if (hasTabData(TAB_SUSPECT_TO_WITNESS)) tabs.push(TAB_SUSPECT_TO_WITNESS);
    if (hasTabData(TAB_DOUBTFUL)) tabs.push(TAB_DOUBTFUL);
    if (hasTabData(TAB_CHANGE_OF_VENUE)) tabs.push(TAB_CHANGE_OF_VENUE);
    if (hasTabData(TAB_DOESNT_INSTITUTE_CHARGE))
      tabs.push(TAB_DOESNT_INSTITUTE_CHARGE);

    return tabs;
  };

  const tab_for_police = [TAB_INVESTIGATION_FILE, TAB_ABOUT_INVESTIGATION_FILE];

  return (
    <MainLayout>
      {/* Back Button */}
      <button
        type="button"
        onClick={() => navigate("/investigation-files")}
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 mb-4 transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        {t("back")}
      </button>

      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">📁</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("investigation_file_details")}
                {file?.is_case_suspended && (
                  <span className="text-xl font-normal text-gray-500">
                    {" "}
                    ({t("suspended")})
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_review_investigation_file")}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="font-semibold text-red-600">
                {t("crime_ref")}:
              </span>
              <span className="text-gray-900 font-medium">
                {file?.crime_reference_number || "-"}
              </span>
            </div>
            {/* Suspects instead of the incident number: first name +N, all on hover. */}
            <SuspectsChip
              incidentRegistrationNumber={file?.incident_registration_number}
            />
          </div>
        </div>
      </div>

      <TabNav
        active={activeTab}
        tabs={
          loginData?.is_police || loginData?.role === "Member"
            ? tab_for_police
            : buildTabs()
        }
        onSelect={setActiveTab}
      />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {activeTab === TAB_INVESTIGATION_FILE ? (
            <>
              <InvestigationInformationTab file={file} setRefetch={setRefetch} />
              {(file?.id || (file as any)?.uu_id) && (
                <DirectorNotesButton
                  model={FEEDBACK_MODEL.investigationFile}
                  objectId={(file?.id || (file as any)?.uu_id) as any}
                  role={
                    isProsecutorRole
                      ? "prosecutor"
                      : isRegistrarRole
                        ? "registrar"
                        : isCoordinator
                          ? "coordinator"
                          : undefined
                  }
                />
              )}
            </>
          ) : activeTab === TAB_ASSIGNED_TO ? (
            <AssignedToTab
              file={file}
              setRefetch={setRefetch}
              setActiveTab={setActiveTab}
              fetchInvestigationFileData={fetchData}
            />
          ) : activeTab === TAB_RE_ASSIGN_PROSECUTOR ? (
            <AssignedToTab
              file={file}
              setRefetch={setRefetch}
              setActiveTab={setActiveTab}
              fetchInvestigationFileData={fetchData}
            />
          ) : activeTab === TAB_ABOUT_INVESTIGATION_FILE ? (
            <IncidentDetail
              crimeReferenceNumber={file?.crime_reference_number}
              incidentRegistrationNumber={file?.incident_registration_number}
              file={file}
            />
          ) : activeTab === TAB_FURTHER_INVESTIGATION ? (
            <FurtherInvestigationTab
              crimeReferenceNumber={file?.crime_reference_number}
              incidentRegistrationNumber={file?.incident_registration_number}
            />
          ) : activeTab === TAB_CHARGE ? (
            <InstituteOfChargeTab
              file={file}
              crimeReferenceNumber={file?.crime_reference_number}
              incidentRegistrationNumber={file?.incident_registration_number}
            />
          ) : activeTab === TAB_SUSPECT_TO_WITNESS ? (
            <SuspectToWitnessTab
              crimeReferenceNumber={file?.crime_reference_number}
              incidentRegistrationNumber={file?.incident_registration_number}
            />
          ) : activeTab === TAB_DOUBTFUL ? (
            <DoubtfulTab
              crimeReferenceNumber={file?.crime_reference_number}
              incidentRegistrationNumber={file?.incident_registration_number}
            />
          ) : activeTab === TAB_CHANGE_OF_VENUE ? (
            <ChangeOfVenueTab
              crimeReferenceNumber={file?.crime_reference_number}
              incidentRegistrationNumber={file?.incident_registration_number}
            />
          ) : activeTab === TAB_DOESNT_INSTITUTE_CHARGE ? (
            <DoesntChargeTab
              file={file}
              fetchData={() => fetchData(state.id)}
            />
          ) : activeTab === TAB_PRIVATE_PRISECUTION ? (
            <PrivateProsecutionTab
              file={file}
              crimeReferenceNumber={file?.crime_reference_number}
              incidentRegistrationNumber={file?.incident_registration_number}
            />
          ) : null}
        </div>
      )}
    </MainLayout>
  );
};

export default InvestigationFileDetail;
